import type { CutoutDefinition } from "@project/emerge";
import type { IListUserOption } from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import MakerJS from "makerjs";
import type { AddableGridInfos } from "../interfaces/AddableGridInfos";

/**
 * Generated stand-in for an Ausschnitt. It is not an option-database article:
 * the hole is built from the width, height and corner radius on the Anbauteil.
 * `side: TOP` matches the other supply-part cutouts, whose local up-axis is
 * rotated onto the panel by the addable build step.
 */
export interface CutoutSelection extends IListUserOption<"Cutout"> {
  width: number;
  height: number;
  cornerRadius: number;
  cutX: number;
  cutY: number;
  cutouts: CutoutDefinition[];
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Centered rounded rectangle, same winding as the other MakerJS cutout paths. */
export function roundedRectangleCutoutPath(
  width: number,
  height: number,
  cornerRadius: number,
): string {
  const model = new MakerJS.models.RoundRectangle(width, height, cornerRadius);
  const pathData = MakerJS.exporter.toSVGPathData(MakerJS.model.center(model), {
    origin: [0, 0],
  });
  return typeof pathData === "string" ? pathData : "";
}

/**
 * Hole size of an Ausschnitt, in mm. `null` when width or height is missing or
 * not positive, so a half-typed size does not punch a hole. The build step and
 * the drag helpers both read the size through this function.
 */
export function cutoutSize(option: {
  width?: unknown;
  height?: unknown;
}): { width: number; height: number } | null {
  const width = finiteNumber(option.width);
  const height = finiteNumber(option.height);
  if (width === null || height === null || width <= 0 || height <= 0) {
    return null;
  }
  return { width, height };
}

function formatCutoutSize(size: { width: number; height: number }): string {
  return `${size.width} × ${size.height} mm`;
}

/** "80 × 60 mm", or `null` without a valid size. */
export function describeCutoutSize(option: {
  width?: unknown;
  height?: unknown;
}): string | null {
  const size = cutoutSize(option);
  return size ? formatCutoutSize(size) : null;
}

/**
 * The supply-part selection an Ausschnitt builds, or `null` when it has no
 * valid size (see `cutoutSize`). The radius is clamped to the smaller
 * half-side; MakerJS does the same.
 */
export function buildCutoutSelection(
  option: Pick<AddableGridInfos, "width" | "height" | "cornerRadius">,
): CutoutSelection | null {
  const size = cutoutSize(option);
  if (!size) return null;
  const { width, height } = size;
  const requestedRadius = finiteNumber(option.cornerRadius) ?? 0;
  const cornerRadius = Math.min(
    Math.max(requestedRadius, 0),
    width / 2,
    height / 2,
  );
  const path = roundedRectangleCutoutPath(width, height, cornerRadius);
  if (!path) return null;
  return {
    kind: "Cutout",
    name: "Ausschnitt",
    description: formatCutoutSize(size),
    deleted: false,
    descriptionEn: "",
    availableExternally: false,
    width,
    height,
    cornerRadius,
    cutX: width,
    cutY: height,
    cutouts: [{ cut: 0, path, side: Sides.TOP }],
  };
}
